/* eslint-disable max-len */
import { AxiosRequestHeaders } from 'axios';
import { IntlShape } from 'react-intl';
import { axios } from '../interceptors';
import { UtilService } from './util-service';
import {
  Legend as LegendType, VisualizerChartRoot, VisualizerPlanData, VisualizerMonthData, VisualizerDetailsMonthData, VisualizerDetailsPeriodData,
} from '../models';
import { ChartService } from './chart-service';

class VisualizerService {
  private static baseUrl = '/visualizer';

  private static chartOrder: string[] = ['superOffPeak', 'low', 'regular', 'high'];

  public static referenceArray: string[] = ['12AM', '1AM', '2AM', '3AM', '4AM', '5AM', '6AM', '7AM', '8AM', '9AM', '10AM', '11AM', '12PM', '1PM', '2PM', '3PM', '4PM', '5PM', '6PM', '7PM', '8PM', '9PM', '10PM', '11PM', '12AM'];

  public static labelOrder: string[] = ['all', 'weekdays', 'weekends'];

  public static getVisualizerOverviewData = async (
    servicePointId: string,
    featureEnabled: boolean | undefined,
    billingCycleFeatureEnabled: boolean | undefined,
  ) => {
    let headers = UtilService.addFeatureHeader({}, featureEnabled, 'ENERGY_USAGE_VISUALIZER') as AxiosRequestHeaders | undefined;
    headers = UtilService.addFeatureHeader(headers, billingCycleFeatureEnabled, 'ENERGY_USAGE_VISUALIZER_BILLING_CYCLE') as AxiosRequestHeaders | undefined;
    const response = await axios.get<VisualizerPlanData<VisualizerMonthData>[]>(`${this.baseUrl}/bars`, {
      params: {
        sp: servicePointId,
      },
      headers,
    });
    return response.data;
  };

  public static getVisualizerDetailsData = async (
    servicePointId: string,
    featureEnabled: boolean | undefined,
    billingCycleFeatureEnabled: boolean | undefined,
  ) => {
    let headers = UtilService.addFeatureHeader({}, featureEnabled, 'ENERGY_USAGE_VISUALIZER') as AxiosRequestHeaders | undefined;
    headers = UtilService.addFeatureHeader(headers, billingCycleFeatureEnabled, 'ENERGY_USAGE_VISUALIZER_BILLING_CYCLE') as AxiosRequestHeaders | undefined;
    const response = await axios.get<VisualizerPlanData<VisualizerDetailsMonthData>[]>(`${this.baseUrl}/details`, {
      params: {
        sp: servicePointId,
      },
      headers,
    });
    return response.data;
  };

  public static convertMonthToChartData(month: VisualizerMonthData, intl: IntlShape, index: number = 0): VisualizerChartRoot {
    const newRoot: VisualizerChartRoot = {
      labels: [''],
      datasets: [],
    };

    const peakData: { [key: string]: number[] } = {};

    month.usages.forEach((usage) => {
      const dataValue = usage.data[index];
      if (dataValue !== 0) {
        if (!peakData[usage.peak]) {
          peakData[usage.peak] = [];
        }
        peakData[usage.peak].push(dataValue);
      }
    });

    VisualizerService.chartOrder.forEach((peak) => {
      if (peakData[peak] && peakData[peak].length > 0) {
        newRoot.datasets.push({
          label: intl.formatMessage({ id: `visualizer-peak-label.${peak}` }),
          data: peakData[peak],
          backgroundColor: ChartService.getMappedColor(peak),
          borderRadius: {
            topLeft: 0,
            bottomLeft: 0,
            topRight: 0,
            bottomRight: 0,
          },
        });
      }
    });

    const radiusIndex = 0;
    if (newRoot.datasets.length > 0) {
      newRoot.datasets[radiusIndex].borderRadius = { topLeft: 10, bottomLeft: 10 };
      if (newRoot.datasets.length > 1) {
        newRoot.datasets[newRoot.datasets.length - 1].borderRadius = { topRight: 10, bottomRight: 10 };
      } else {
        newRoot.datasets[radiusIndex].borderRadius = {
          topLeft: 10,
          bottomLeft: 10,
          topRight: 10,
          bottomRight: 10,
        };
      }
    }

    return newRoot;
  }

  public static calculatelargestWidth(data: VisualizerPlanData<VisualizerMonthData>, index: number = 0) {
    let largest = 0;
    data.data.forEach((month) => {
      let sum = 0;
      month.usages.forEach((usage) => {
        sum += usage.data[index];
      });
      if (sum > largest) {
        largest = sum;
      }
    });
    return largest;
  }

  public static getLegend(data: VisualizerMonthData[], intl: IntlShape): LegendType[] {
    const legend: LegendType[] = [];

    this.chartOrder.forEach((peak) => {
      const datasetExists = data.some((month) => month.usages.some(
        (usage) => usage.peak === peak && usage.data.some((value) => value !== 0),
      ));

      if (datasetExists) {
        const color = ChartService.getMappedColor(peak);
        legend.push({ label: intl.formatMessage({ id: `visualizer-legend.${peak}` }), color });
      }
    });

    return legend;
  }

  public static mapTicks(period: VisualizerDetailsPeriodData) {
    const ticksArray = ['12AM', '', '', '', '', '', '', '', '', '', '', '', '12PM', '', '', '', '', '', '', '', '', '', '', '', '12AM'];
    let lastLabel = '';
    period.hours.forEach((hour, index) => {
      const [curLabel] = hour;
      if (lastLabel !== curLabel) {
        ticksArray[index] = this.referenceArray[index];
        lastLabel = curLabel;
      }
    });
    return ticksArray;
  }

  public static sortPeriods(periods: VisualizerDetailsPeriodData[]) {
    function customSort(a: VisualizerDetailsPeriodData, b: VisualizerDetailsPeriodData): number {
      return VisualizerService.labelOrder.indexOf(a.label) - VisualizerService.labelOrder.indexOf(b.label);
    }

    const sortedData: VisualizerDetailsPeriodData[] = periods.slice().sort(customSort);
    return sortedData;
  }

  public static calculateAnnualUsage(planData: VisualizerPlanData<VisualizerMonthData>) {
    const sum = planData.data.reduce((planSum, month) => planSum + month.usages.reduce((monthSum, hour) => monthSum + hour.data[0], 0), 0);
    return Math.round(sum);
  }

  public static shouldShowComponent = (errorCode: string | undefined): boolean => (
    errorCode === 'SWITCH_IN_PROGRESS'
      || errorCode === 'RECENTLY_SWITCHED_TO_NEW_PLAN'
      || !errorCode
  );
}

export { VisualizerService };
